import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtEur, fmtNum, diffTone } from "@/lib/format";

export const BulkReimbursementDialog = ({ open, stations, initial, onClose, onDone }) => {
  const [f, setF] = useState({ date_from: "", date_to: "", station_id: "all", rate: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ ...initial, rate: "" }); setPreview(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v })); setPreview(null); };

  const send = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post("/entries/reimbursement-bulk", { ...f, station_id: f.station_id === "all" ? null : f.station_id, rate: Number(f.rate), confirm });
      if (confirm) { toast.success(`Tariffa aggiornata su ${data.updated} giorni`); onDone(); } else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-reimb-dialog">
        <DialogHeader>
          <DialogTitle>Rimborso massivo</DialogTitle>
          <DialogDescription>Nuova tariffa €/km applicata ai giorni del periodo che hanno già i km rimborsati. L'importo si ricalcola: km × tariffa.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.date_from} onChange={set("date_from")} data-testid="bulk-reimb-from" /></div>
          <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={f.date_to} onChange={set("date_to")} data-testid="bulk-reimb-to" /></div>
          <div className="space-y-1.5"><Label>Stazione</Label>
            <Select value={f.station_id} onValueChange={set("station_id")}>
              <SelectTrigger data-testid="bulk-reimb-station"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le stazioni</SelectItem>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nuova tariffa (€/km)</Label><Input type="number" step="0.001" min="0" placeholder="es. 0,215" value={f.rate} onChange={set("rate")} data-testid="bulk-reimb-rate" /></div>
        </div>
        {preview && (
          <div className="space-y-2 rounded-lg bg-slate-50 p-4 text-sm" data-testid="bulk-reimb-preview">
            <p><b data-testid="bulk-reimb-count">{preview.updated}</b> giorni da aggiornare ({fmtNum(preview.km, 1)} km) · {preview.skipped} saltati senza km rimborsati</p>
            {preview.old_rates.length > 0 && <p className="text-xs text-slate-500">Tariffe attuali: {preview.old_rates.length > 3 ? `da ${fmtNum(preview.old_rates[0], 3)} a ${fmtNum(preview.old_rates[preview.old_rates.length - 1], 3)} €/km (${preview.old_rates.length} tariffe diverse)` : preview.old_rates.map((r) => `${fmtNum(r, 3)} €/km`).join(", ")}</p>}
            <div className="grid grid-cols-3 gap-3">
              <div><p className="text-xs text-slate-500">Rimborso attuale</p><p className="font-mono-num font-semibold">{fmtEur(preview.old_total)}</p></div>
              <div><p className="text-xs text-slate-500">Nuovo rimborso</p><p className="font-mono-num font-semibold" data-testid="bulk-reimb-new-total">{fmtEur(preview.new_total)}</p></div>
              <div><p className="text-xs text-slate-500">Variazione</p><p className={`font-mono-num font-semibold ${diffTone(preview.delta)}`}>{fmtEur(preview.delta)}</p></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          {!preview
            ? <Button onClick={() => send(false)} disabled={busy || !f.date_from || !f.date_to || !(Number(f.rate) > 0)} data-testid="bulk-reimb-preview-button">Anteprima</Button>
            : <Button onClick={() => send(true)} disabled={busy || !preview.updated} data-testid="bulk-reimb-confirm-button">Applica a {preview.updated} giorni</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
